import React, { useState, useEffect } from 'react';
import { Plus, Wifi, Edit, Trash2, Search, Download, Upload, X, Save, Check, FileText, MapPin, Building2, History, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Cable, Radio, Network } from 'lucide-react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../hooks/useNotifications';
import { useLanguage } from '../contexts/LanguageContext';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
// @ts-ignore
import 'jspdf-autotable';
import Modal from './Modal';

interface Circuit {
  id: number;
  service_provider: string;
  package_name: string;
  circuit_name: string;
  download_speed: number;
  upload_speed: number;
  price: number;
  infrastructure: 'Fiber Optik' | 'Radio Link' | 'ADSL' | 'VDSL' | 'VDSL2+';
  address: string;
  location_id: number;
  location_name?: string;
  contract_file_path?: string;
  contract_filename?: string;
  status: 'active' | 'inactive' | 'suspended';
  start_date?: string;
  end_date?: string;
  notes?: string;
  created_by: number;
  created_by_name?: string;
  updated_by?: number;
  updated_by_name?: string;
  created_at: string;
  updated_at?: string;
}

interface Location {
  id: number;
  name: string;
  address: string;
  phone: string;
}

export default function CircuitsPage() {
  const { showSuccess, showError, showInfo } = useNotifications();
  const { t, language } = useLanguage();
  const [circuits, setCircuits] = useState<Circuit[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCircuit, setEditingCircuit] = useState<Circuit | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'suspended'>('all');
  const [infrastructureFilter, setInfrastructureFilter] = useState<string>('all');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showContractModal, setShowContractModal] = useState(false);
  const [selectedCircuit, setSelectedCircuit] = useState<Circuit | null>(null);
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [uploadingContract, setUploadingContract] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [circuitHistory, setCircuitHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    service_provider: '',
    package_name: '',
    circuit_name: '',
    download_speed: '',
    upload_speed: '',
    price: '',
    infrastructure: 'Fiber Optik' as Circuit['infrastructure'],
    address: '',
    location_id: '',
    start_date: '',
    end_date: '',
    notes: '',
    status: 'active' as Circuit['status']
  });

  useEffect(() => {
    fetchCircuits();
    fetchLocations();
  }, []);

  // Mesajları otomatik temizle
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const fetchCircuits = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCircuits();
      setCircuits(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching circuits:', error);
      setError(t('circuits.errLoad', { msg: error instanceof Error ? error.message : String(error) }));
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await apiService.getLocations();
      setLocations(Array.isArray(response) ? response : response?.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      service_provider: '',
      package_name: '',
      circuit_name: '',
      download_speed: '',
      upload_speed: '',
      price: '',
      infrastructure: 'Fiber Optik',
      address: '',
      location_id: '',
      start_date: '',
      end_date: '',
      notes: '',
      status: 'active'
    });
    setEditingCircuit(null);
    setShowModal(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);
      setSuccess(null);

      const circuitData = {
        ...formData,
        download_speed: parseFloat(formData.download_speed),
        upload_speed: parseFloat(formData.upload_speed),
        price: parseFloat(formData.price),
        location_id: parseInt(formData.location_id),
        start_date: formData.start_date || null,
        end_date: formData.end_date || null
      };

      if (editingCircuit) {
        await apiService.updateCircuit(editingCircuit.id, circuitData);
        setSuccess(t('circuits.successUpdate'));
        showSuccess(t('circuits.notifUpdated'), t('circuits.notifUpdatedMsg', { name: formData.circuit_name }));
      } else {
        await apiService.createCircuit(circuitData);
        setSuccess(t('circuits.successCreate'));
        showSuccess(t('circuits.notifCreated'), t('circuits.notifCreatedMsg', { name: formData.circuit_name }));
      }

      await fetchCircuits();
      resetForm();
    } catch (error) {
      console.error('Error saving circuit:', error);
      setError(t('circuits.errSave', { msg: error instanceof Error ? error.message : String(error) }));
    }
  };

  const handleEdit = (circuit: Circuit) => {
    setEditingCircuit(circuit);
    
    // Tarih formatını düzelt (YYYY-MM-DD formatına çevir)
    const formatDate = (dateString: string | undefined) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      } catch {
        return '';
      }
    };
    
    setFormData({
      service_provider: circuit.service_provider,
      package_name: circuit.package_name,
      circuit_name: circuit.circuit_name,
      download_speed: circuit.download_speed.toString(),
      upload_speed: circuit.upload_speed.toString(),
      price: circuit.price.toString(),
      infrastructure: circuit.infrastructure,
      address: circuit.address,
      location_id: circuit.location_id.toString(),
      start_date: formatDate(circuit.start_date),
      end_date: formatDate(circuit.end_date),
      notes: circuit.notes || '',
      status: circuit.status
    });
    setShowModal(true);
  };

  const handleDelete = async (circuit: Circuit) => {
    if (!confirm(t('circuits.confirmDelete', { name: circuit.circuit_name }))) {
      return;
    }

    try {
      await apiService.deleteCircuit(circuit.id);
      setSuccess(t('circuits.successDelete'));
      showSuccess(t('circuits.notifDeleted'), t('circuits.notifDeletedMsg', { name: circuit.circuit_name }));
      await fetchCircuits();
    } catch (error) {
      console.error('Error deleting circuit:', error);
      setError(t('circuits.errDelete', { msg: error instanceof Error ? error.message : String(error) }));
      showError(t('circuits.notifDeleteErr'), t('circuits.notifDeleteErrMsg'));
    }
  };

  const handleContractUpload = async () => {
    if (!contractFile || !selectedCircuit) {
      setError(t('circuits.errContractSelect'));
      return;
    }

    try {
      setUploadingContract(true);
      setError(null);

      const formData = new FormData();
      formData.append('contract', contractFile);

      await apiService.uploadCircuitContract(selectedCircuit.id, formData);
      setSuccess(t('circuits.successContractUpload'));
      await fetchCircuits();
      setShowContractModal(false);
      setContractFile(null);
      setSelectedCircuit(null);
    } catch (error) {
      console.error('Error uploading contract:', error);
      setError(t('circuits.errContractUpload', { msg: error instanceof Error ? error.message : String(error) }));
    } finally {
      setUploadingContract(false);
    }
  };

  const handleContractDownload = async (circuit: Circuit) => {
    try {
      await apiService.downloadCircuitContract(circuit.id);
    } catch (error) {
      console.error('Error downloading contract:', error);
      setError(t('circuits.errContractDownload', { msg: error instanceof Error ? error.message : String(error) }));
    }
  };

  const handleShowHistory = async (circuit: Circuit) => {
    try {
      setLoadingHistory(true);
      setSelectedCircuit(circuit);
      
      // Backend'den circuit history'yi çek
      const response = await fetch(`${import.meta.env.VITE_API_URL}/circuits/${circuit.id}/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const history = await response.json();
        const historyData = history.data || [];
        
        // Parse JSON changes if they are strings
        const parsedHistory = historyData.map((item: any) => {
          if (item.changes && typeof item.changes === 'string') {
            try {
              item.changes = JSON.parse(item.changes) || {};
            } catch (e) {
              console.error('Error parsing changes JSON:', e);
              item.changes = {};
            }
          }
          return item;
        });
        
        setCircuitHistory(parsedHistory);
      } else {
        setCircuitHistory([]);
      }
      
      setShowHistoryModal(true);
    } catch (error) {
      console.error('Error fetching circuit history:', error);
      setError(t('circuits.errHistory'));
      setCircuitHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Filtreleme
  const filteredCircuits = circuits.filter(circuit => {
    const matchesSearch = String(circuit.circuit_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         String(circuit.service_provider || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         String(circuit.package_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         String(circuit.address || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || circuit.status === statusFilter;
    
    // Infrastructure filtreleme - özel durum: adsl_vdsl tüm ADSL/VDSL/VDSL2+ tiplerini gösterir
    let matchesInfrastructure = true;
    if (infrastructureFilter === 'all') {
      matchesInfrastructure = true;
    } else if (infrastructureFilter === 'adsl_vdsl') {
      matchesInfrastructure = ['ADSL', 'VDSL', 'VDSL2+'].includes(circuit.infrastructure);
    } else {
      matchesInfrastructure = circuit.infrastructure === infrastructureFilter;
    }
    
    const matchesLocation = locationFilter === 'all' || circuit.location_id.toString() === locationFilter;

    return matchesSearch && matchesStatus && matchesInfrastructure && matchesLocation;
  });

  // Sıralama
  const sortedCircuits = [...filteredCircuits].sort((a, b) => {
    if (!sortField) return 0;

    let aValue: any = a[sortField as keyof Circuit];
    let bValue: any = b[sortField as keyof Circuit];

    // Özel alanlar için dönüşüm
    if (sortField === 'download_speed' || sortField === 'upload_speed' || sortField === 'price') {
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
    } else if (typeof aValue === 'string') {
      aValue = String(aValue || '').toLowerCase();
      bValue = String(bValue || '').toLowerCase();
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedCircuits.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCircuits = sortedCircuits.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, infrastructureFilter, locationFilter, sortField, sortDirection]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 ml-1 inline" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 inline" />
      : <ArrowDown className="w-3 h-3 ml-1 inline" />;
  };

  // Export functions
  const exportToExcel = () => {
    try {
      const locale = language === 'en' ? 'en-US' : 'tr-TR';
      const exportData = filteredCircuits.map((circuit, index) => ({
        [t('circuits.excelSeqNo')]: index + 1,
        [t('circuits.excelCircuitName')]: circuit.circuit_name,
        [t('circuits.excelAddress')]: circuit.address,
        [t('circuits.excelServiceProvider')]: circuit.service_provider,
        [t('circuits.excelPackage')]: circuit.package_name,
        [t('circuits.excelDownloadSpeed')]: circuit.download_speed,
        [t('circuits.excelUploadSpeed')]: circuit.upload_speed,
        [t('circuits.excelTotalSpeed')]: `${circuit.download_speed}/${circuit.upload_speed} Mbps`,
        [t('circuits.excelInfrastructure')]: circuit.infrastructure,
        [t('circuits.excelLocation')]: circuit.location_name || '',
        [t('circuits.excelStatus')]: getStatusText(circuit.status),
        [t('circuits.excelPrice')]: circuit.price,
        [t('circuits.excelStartDate')]: circuit.start_date ? new Date(circuit.start_date).toLocaleDateString(locale) : '',
        [t('circuits.excelEndDate')]: circuit.end_date ? new Date(circuit.end_date).toLocaleDateString(locale) : '',
        [t('circuits.excelNotes')]: circuit.notes || '',
        [t('circuits.excelCreatedAt')]: new Date(circuit.created_at).toLocaleDateString(locale)
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('circuits.excelSheet'));
      
      // Set column widths
      const colWidths = [
        { wch: 8 },  // Sıra No
        { wch: 20 }, // Devre Adı
        { wch: 20 }, // Adres
        { wch: 18 }, // Servis Sağlayıcı
        { wch: 15 }, // Paket
        { wch: 15 }, // Download Hızı
        { wch: 15 }, // Upload Hızı
        { wch: 18 }, // Hız (Toplam)
        { wch: 12 }, // Altyapı
        { wch: 20 }, // Lokasyon
        { wch: 10 }, // Durum
        { wch: 12 }, // Fiyat
        { wch: 15 }, // Başlangıç Tarihi
        { wch: 15 }, // Bitiş Tarihi
        { wch: 30 }, // Notlar
        { wch: 18 }  // Oluşturulma Tarihi
      ];
      ws['!cols'] = colWidths;

      const filename = `devreler-${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, filename);
      
      showSuccess(t('circuits.success'), t('circuits.excelSuccess', { count: filteredCircuits.length }));
    } catch (error: any) {
      console.error('Excel export error:', error);
      showError(t('circuits.error'), t('circuits.excelError'));
    }
  };

  const exportToPDF = () => {
    try {
      const pdf = new jsPDF('l', 'mm', 'a4'); // landscape orientation
      
      const locale = language === 'en' ? 'en-US' : 'tr-TR';
      // Add title
      pdf.setFontSize(16);
      pdf.text(t('circuits.pdfTitle'), 14, 15);

      // Add date
      pdf.setFontSize(10);
      pdf.text(t('circuits.pdfReportDate', { date: new Date().toLocaleDateString(locale) }), 14, 22);
      pdf.text(t('circuits.pdfTotalCircuits', { count: filteredCircuits.length }), 200, 22);
      
      // Prepare table data
      const tableData = filteredCircuits.map((circuit, index) => [
        (index + 1).toString(),
        circuit.circuit_name,
        circuit.service_provider,
        circuit.package_name,
        `${circuit.download_speed}/${circuit.upload_speed} Mbps`,
        circuit.infrastructure,
        circuit.location_name || '',
        getStatusText(circuit.status)
      ]);

      // Add table using autoTable plugin
      (pdf as any).autoTable({
        head: [[t('circuits.pdfColSeq'), t('circuits.pdfColCircuit'), t('circuits.pdfColProvider'), t('circuits.pdfColPackage'), t('circuits.pdfColSpeed'), t('circuits.pdfColInfrastructure'), t('circuits.pdfColLocation'), t('circuits.pdfColStatus')]],
        body: tableData,
        startY: 28,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 35 },
          2: { cellWidth: 30 },
          3: { cellWidth: 25 },
          4: { cellWidth: 25 },
          5: { cellWidth: 25 },
          6: { cellWidth: 30 },
          7: { cellWidth: 20 }
        },
        margin: { top: 28, left: 14, right: 14 }
      });

      // Add summary on new page if needed
      if (filteredCircuits.length > 0) {
        const activeCount = filteredCircuits.filter(c => c.status === 'active').length;
        const totalPrice = filteredCircuits.reduce((sum, c) => sum + c.price, 0);
        
        pdf.addPage();
        pdf.setFontSize(14);
        pdf.text(t('circuits.pdfSummaryTitle'), 14, 15);
        pdf.setFontSize(10);

        let yPos = 25;
        pdf.text(t('circuits.pdfTotalCircuits', { count: filteredCircuits.length }), 14, yPos);
        yPos += 8;
        pdf.text(t('circuits.pdfActive', { count: activeCount }), 14, yPos);
        yPos += 8;
        pdf.text(t('circuits.pdfTotalCost', { amount: totalPrice.toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2 }) }), 14, yPos);
      }

      const filename = `devreler-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);
      
      showSuccess(t('circuits.success'), t('circuits.pdfSuccess', { count: filteredCircuits.length }));
    } catch (error: any) {
      console.error('PDF export error:', error);
      showError(t('circuits.error'), t('circuits.pdfError'));
    }
  };

  const getStatusColor = (status: Circuit['status']) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      case 'suspended': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getStatusText = (status: Circuit['status']) => {
    switch (status) {
      case 'active': return t('circuits.statusActive');
      case 'inactive': return t('circuits.statusInactive');
      case 'suspended': return t('circuits.statusSuspended');
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Wifi className="w-6 h-6" />
          {t('circuits.title')}
        </h1>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <button
            onClick={exportToExcel}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700 text-sm"
            title="Excel"
          >
            <Download className="w-4 h-4" />
            Excel
          </button>
          <button
            onClick={exportToPDF}
            className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-red-700 text-sm"
            title="PDF"
          >
            <FileText className="w-4 h-4" />
            PDF
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="bg-primary text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-dark text-sm"
          >
            <Plus className="w-4 h-4" />
            {t('circuits.btnNew')}
          </button>
        </div>
      </div>

      {/* Hata ve Başarı Mesajları */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
        </div>
      )}

      {/* Altyapı Kartları */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6">
        {/* Fiber Devreler Kartı */}
        <button
          onClick={() => setInfrastructureFilter(infrastructureFilter === 'Fiber Optik' ? 'all' : 'Fiber Optik')}
          className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-6 text-left transition-all hover:shadow-md ${
            infrastructureFilter === 'Fiber Optik' 
              ? 'border-primary ring-2 ring-primary/30' 
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:border-gray-600'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('circuits.cardFiber')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {circuits.filter(c => c.infrastructure === 'Fiber Optik').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
              <Cable className="w-6 h-6 text-primary" />
            </div>
          </div>
        </button>

        {/* Radio Link Devreler Kartı */}
        <button
          onClick={() => setInfrastructureFilter(infrastructureFilter === 'Radio Link' ? 'all' : 'Radio Link')}
          className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-6 text-left transition-all hover:shadow-md ${
            infrastructureFilter === 'Radio Link' 
              ? 'border-primary ring-2 ring-primary/30' 
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:border-gray-600'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('circuits.cardRadioLink')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {circuits.filter(c => c.infrastructure === 'Radio Link').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Radio className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </button>

        {/* Adsl&Vdsl Devreler Kartı */}
        <button
          onClick={() => setInfrastructureFilter(infrastructureFilter === 'adsl_vdsl' ? 'all' : 'adsl_vdsl')}
          className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-6 text-left transition-all hover:shadow-md ${
            infrastructureFilter === 'adsl_vdsl'
              ? 'border-primary ring-2 ring-primary/30' 
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:border-gray-600'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('circuits.cardAdslVdsl')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {circuits.filter(c => ['ADSL', 'VDSL', 'VDSL2+'].includes(c.infrastructure)).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Network className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </button>
      </div>

      {/* Arama ve Filtreler */}
      <div className="mb-6 space-y-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            placeholder={t('circuits.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border rounded-lg w-full max-w-md"
          />
        </div>

        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('circuits.filterTitle')}</h3>
            <button
              onClick={() => {
                setStatusFilter('all');
                setInfrastructureFilter('all');
                setLocationFilter('all');
                setSearchTerm('');
              }}
              className="text-xs text-primary hover:text-primary-dark"
            >
              {t('circuits.filterClearAll')}
            </button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('circuits.filterStatus')}</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="all">{t('circuits.filterAll')}</option>
                <option value="active">{t('circuits.statusActive')}</option>
                <option value="inactive">{t('circuits.statusInactive')}</option>
                <option value="suspended">{t('circuits.statusSuspended')}</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('circuits.filterInfrastructure')}</label>
              <select
                value={infrastructureFilter}
                onChange={(e) => setInfrastructureFilter(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="all">{t('circuits.filterAll')}</option>
                <option value="Fiber Optik">Fiber Optik</option>
                <option value="Radio Link">Radio Link</option>
                <option value="adsl_vdsl">{t('circuits.filterAdslVdslAll')}</option>
                <option value="ADSL">ADSL</option>
                <option value="VDSL">VDSL</option>
                <option value="VDSL2+">VDSL2+</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('circuits.filterLocation')}</label>
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="all">{t('circuits.filterAll')}</option>
                {locations.map(location => (
                  <option key={location.id} value={location.id.toString()}>{location.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Devre Listesi */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('circuits.colSeqNo')}</th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none"
                  onClick={() => handleSort('circuit_name')}
                >
                  {t('circuits.colCircuitName')} {getSortIcon('circuit_name')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none"
                  onClick={() => handleSort('service_provider')}
                >
                  {t('circuits.colServiceProvider')} {getSortIcon('service_provider')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none"
                  onClick={() => handleSort('package_name')}
                >
                  {t('circuits.colPackage')} {getSortIcon('package_name')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none"
                  onClick={() => handleSort('download_speed')}
                >
                  {t('circuits.colSpeed')} {getSortIcon('download_speed')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none"
                  onClick={() => handleSort('infrastructure')}
                >
                  {t('circuits.colInfrastructure')} {getSortIcon('infrastructure')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none"
                >
                  {t('circuits.colLocation')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none"
                  onClick={() => handleSort('status')}
                >
                  {t('circuits.colStatus')} {getSortIcon('status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('circuits.colActions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedCircuits.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center">
                    <Wifi className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">{t('circuits.emptyState')}</p>
                  </td>
                </tr>
              ) : (
                paginatedCircuits.map((circuit, index) => (
                <tr key={circuit.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {startIndex + index + 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{circuit.circuit_name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{circuit.address}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {circuit.service_provider}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {circuit.package_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    <div>↓ {circuit.download_speed} Mbps</div>
                    <div>↑ {circuit.upload_speed} Mbps</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {circuit.infrastructure}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {circuit.location_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(circuit.status)}`}>
                      {getStatusText(circuit.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(circuit)}
                        className="text-primary hover:text-primary-dark"
                        title={t('circuits.actionEdit')}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleShowHistory(circuit)}
                        className="text-purple-600 hover:text-purple-900"
                        title={t('circuits.actionHistory')}
                      >
                        <History className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(circuit)}
                        className="text-red-600 hover:text-red-900"
                        title={t('circuits.actionDelete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {circuit.contract_file_path ? (
                        <button
                          onClick={() => handleContractDownload(circuit)}
                          className="text-green-600 hover:text-green-900"
                          title={t('circuits.actionDownloadContract')}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setSelectedCircuit(circuit);
                            setShowContractModal(true);
                          }}
                          className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100"
                          title={t('circuits.actionUploadContract')}
                        >
                          <Upload className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Mobile: Simple pagination */}
              <div className="flex sm:hidden items-center justify-between w-full">
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  {t('circuits.paginationPrev')}
                </button>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {t('circuits.paginationPage', { current: currentPage, total: totalPages })}
                </span>
                <button
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('circuits.paginationNext')}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>

              {/* Desktop: Full pagination */}
              <div className="hidden sm:flex items-center justify-between w-full">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  {t('circuits.paginationInfo', { total: sortedCircuits.length, start: startIndex + 1, end: Math.min(endIndex, sortedCircuits.length) })}
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    {t('circuits.paginationPrev')}
                  </button>

                  {/* Page numbers */}
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            currentPage === pageNum
                              ? 'bg-primary text-white'
                              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('circuits.paginationNext')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {filteredCircuits.length === 0 && (
          <div className="text-center py-12">
            <Wifi className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{t('circuits.emptyTitle')}</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">{t('circuits.emptyDesc')}</p>
            <button
              onClick={() => setShowModal(true)}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark flex items-center gap-2 mx-auto"
            >
              <Plus className="w-4 h-4" />
              {t('circuits.emptyBtn')}
            </button>
          </div>
        )}
      </div>

      {/* Devre Ekleme/Düzenleme Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {editingCircuit ? t('circuits.formTitleEdit') : t('circuits.formTitleNew')}
              </h2>
              <button
                onClick={resetForm}
                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Servis Sağlayıcı */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('circuits.labelServiceProvider')}
                  </label>
                  <input
                    type="text"
                    value={formData.service_provider}
                    onChange={(e) => setFormData({...formData, service_provider: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>

                {/* Paket Adı */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('circuits.labelPackageName')}
                  </label>
                  <input
                    type="text"
                    value={formData.package_name}
                    onChange={(e) => setFormData({...formData, package_name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>

                {/* Devre Adı */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('circuits.labelCircuitName')}
                  </label>
                  <input
                    type="text"
                    value={formData.circuit_name}
                    onChange={(e) => setFormData({...formData, circuit_name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>

                {/* Download Hızı */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('circuits.labelDownloadSpeed')}
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.download_speed}
                    onChange={(e) => setFormData({...formData, download_speed: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>

                {/* Upload Hızı */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('circuits.labelUploadSpeed')}
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.upload_speed}
                    onChange={(e) => setFormData({...formData, upload_speed: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>

                {/* Fiyat */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('circuits.labelPrice')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>

                {/* Altyapı */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('circuits.labelInfrastructure')}
                  </label>
                  <select
                    value={formData.infrastructure}
                    onChange={(e) => setFormData({...formData, infrastructure: e.target.value as Circuit['infrastructure']})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  >
                    <option value="Fiber Optik">Fiber Optik</option>
                    <option value="Radio Link">Radio Link</option>
                    <option value="ADSL">ADSL</option>
                    <option value="VDSL">VDSL</option>
                    <option value="VDSL2+">VDSL2+</option>
                  </select>
                </div>

                {/* Adres */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('circuits.labelAddress')}
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="10.1.3.4/30"
                    required
                  />
                </div>

                {/* Lokasyon */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('circuits.labelLocation')}
                  </label>
                  <select
                    value={formData.location_id}
                    onChange={(e) => setFormData({...formData, location_id: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  >
                    <option value="">{t('circuits.placeholderLocation')}</option>
                    {locations.map(location => (
                      <option key={location.id} value={location.id}>{location.name}</option>
                    ))}
                  </select>
                </div>

                {/* Başlangıç Tarihi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('circuits.labelStartDate')}
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                {/* Bitiş Tarihi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('circuits.labelEndDate')}
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                {/* Durum */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('circuits.labelStatus')}
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value as Circuit['status']})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="active">{t('circuits.statusActive')}</option>
                    <option value="inactive">{t('circuits.statusInactive')}</option>
                    <option value="suspended">{t('circuits.statusSuspended')}</option>
                  </select>
                </div>
              </div>

              {/* Notlar */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('circuits.labelNotes')}
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t('circuits.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingCircuit ? t('circuits.update') : t('circuits.create')}
                </button>
              </div>
            </form>
          </div>
        </Modal>

      {/* Sözleşme Yükleme Modal */}
      <Modal isOpen={showContractModal} onClose={() => setShowContractModal(false)}>
          {selectedCircuit && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t('circuits.contractTitle')}
              </h2>
              <button
                onClick={() => {
                  setShowContractModal(false);
                  setContractFile(null);
                  setSelectedCircuit(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <strong>{t('circuits.contractCircuit')}</strong> {selectedCircuit.circuit_name}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>{t('circuits.contractProvider')}</strong> {selectedCircuit.service_provider}
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('circuits.contractFileLabel')}
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setContractFile(e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowContractModal(false);
                    setContractFile(null);
                    setSelectedCircuit(null);
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t('circuits.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleContractUpload}
                  disabled={!contractFile || uploadingContract}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {uploadingContract ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>{t('circuits.contractUploading')}</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      <span>{t('circuits.contractUpload')}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
          )}
        </Modal>

      {/* History Modal */}
      <Modal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)}>
          {selectedCircuit && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t('circuits.historyTitle', { name: selectedCircuit.circuit_name })}
              </h2>
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setSelectedCircuit(null);
                  setCircuitHistory([]);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">{t('circuits.historyLoading')}</span>
                </div>
              ) : circuitHistory.length === 0 ? (
                <div className="text-center py-8">
                  <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{t('circuits.historyEmptyTitle')}</h3>
                  <p className="text-gray-500 dark:text-gray-400">{t('circuits.historyEmptyDesc')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {circuitHistory.map((historyItem, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              historyItem.action === 'created' ? 'bg-green-100 text-green-800' :
                              historyItem.action === 'updated' ? 'bg-primary/20 text-primary-dark' :
                              historyItem.action === 'deleted' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                            }`}>
                              {historyItem.action === 'created' ? t('circuits.historyActionCreated') :
                               historyItem.action === 'updated' ? t('circuits.historyActionUpdated') :
                               historyItem.action === 'deleted' ? t('circuits.historyActionDeleted') :
                               historyItem.action}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {new Date(historyItem.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                            </span>
                          </div>
                          
                          <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                            <strong>{t('circuits.historyUser')}</strong> {historyItem.user_name || t('circuits.historyUnknown')}
                          </div>

                          {historyItem.description && (
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              <strong>{t('circuits.historyDesc')}</strong> {historyItem.description}
                            </div>
                          )}
                          
                          {historyItem.changes && typeof historyItem.changes === 'string' && (
                            <div className="mt-2">
                              <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                                JSON parse edilemedi: {historyItem.changes.substring(0, 100)}
                              </p>
                            </div>
                          )}
                          {historyItem.changes && typeof historyItem.changes === 'object' && Object.keys(historyItem.changes).length > 0 && (
                            <div className="mt-3">
                              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('circuits.historyChanges')}</h4>
                              <div className="space-y-1">
                                {Object.entries(historyItem.changes).filter(([field, change]: [string, any]) => {
                                  if (!change || typeof change !== 'object') return false;
                                  const oldVal = change.old !== null && change.old !== undefined ? String(change.old).trim() : '';
                                  const newVal = change.new !== null && change.new !== undefined ? String(change.new).trim() : '';
                                  return oldVal !== newVal && !(oldVal === '' && newVal === '');
                                }).map(([field, change]: [string, any]) => {
                                  const fieldNames: { [key: string]: string } = {
                                    'service_provider': t('circuits.fieldServiceProvider'),
                                    'package_name': t('circuits.fieldPackageName'),
                                    'circuit_name': t('circuits.fieldCircuitName'),
                                    'download_speed': t('circuits.fieldDownloadSpeed'),
                                    'upload_speed': t('circuits.fieldUploadSpeed'),
                                    'price': t('circuits.fieldPrice'),
                                    'infrastructure': t('circuits.fieldInfrastructure'),
                                    'address': t('circuits.fieldAddress'),
                                    'location_id': t('circuits.fieldLocation'),
                                    'start_date': t('circuits.fieldStartDate'),
                                    'end_date': t('circuits.fieldEndDate'),
                                    'notes': t('circuits.fieldNotes'),
                                    'status': t('circuits.fieldStatus')
                                  };

                                  const fieldLabel = fieldNames[field] || field;
                                  const oldValue = change.old !== null && change.old !== undefined ? String(change.old) : null;
                                  const newValue = change.new !== null && change.new !== undefined ? String(change.new) : null;

                                  const formatStatus = (val: string | null) => {
                                    if (!val) return t('circuits.historyEmpty');
                                    if (val === 'active') return t('circuits.statusActive');
                                    if (val === 'inactive') return t('circuits.statusInactive');
                                    if (val === 'suspended') return t('circuits.statusSuspended');
                                    return val;
                                  };

                                  const displayOld = field === 'status' ? formatStatus(oldValue) : (oldValue || t('circuits.historyEmpty'));
                                  const displayNew = field === 'status' ? formatStatus(newValue) : (newValue || t('circuits.historyEmpty'));
                                  
                                  return (
                                    <div key={field} className="text-sm">
                                      <span className="font-medium text-gray-600 dark:text-gray-400">{fieldLabel}:</span>
                                      <span className="text-red-600 line-through ml-1">{displayOld}</span>
                                      <span className="text-gray-400 mx-1">→</span>
                                      <span className="text-green-600">{displayNew}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}
        </Modal>
    </div>
  );
}
